// src/components/mdx/client/visualizations/substitutionEffectData.ts

/**
 * Shared data utilities for the Substitution Effect chart.
 *
 * These helpers encapsulate the expensive data generation logic so the React
 * component can memoize deterministic scenarios and unit tests can exercise
 * the behaviour directly using seeded random number generators.
 * 
 * The substitution effect demonstrates the performance impact of different
 * creative replacement strategies: abrupt (immediate switch) versus weaned
 * (gradual transition). This data generation simulates realistic creative
 * performance patterns including learning phases and natural volatility.
 */

import type { RandomSource } from '~/lib/utils/random'

/**
 * Data point structure for creative performance during substitution scenarios.
 * 
 * @interface SubstitutionEffectDataPoint
 * @property {number} day - Day number in the timeline (0-indexed)
 * @property {number} fatiguedCreativeSpend - Budget allocated to fatigued creative
 * @property {number} newCreativeSpend - Budget allocated to new creative
 * @property {number} totalSpend - Total budget (constant at 1000)
 * @property {number | null} fatiguedCreativeROAS - ROAS of fatigued creative (null when not active)
 * @property {number | null} newCreativeROAS - ROAS of new creative (null when not active)
 * @property {number} weightedROAS - Weighted average ROAS based on spend allocation
 * @property {number} abruptWeightedROAS - Weighted ROAS for abrupt strategy
 * @property {number} weanedWeightedROAS - Weighted ROAS for weaned strategy
 * @property {boolean} [learningPhase] - Whether this point is in the learning phase
 * @property {number} [abruptRevenue] - Revenue generated by abrupt strategy
 * @property {number} [weanedRevenue] - Revenue generated by weaned strategy
 * @property {number} [revenueDifference] - Revenue difference between strategies
 */
export interface SubstitutionEffectDataPoint {
  day: number
  fatiguedCreativeSpend: number
  newCreativeSpend: number
  totalSpend: number
  fatiguedCreativeROAS: number | null
  newCreativeROAS: number | null
  weightedROAS: number
  abruptWeightedROAS: number
  weanedWeightedROAS: number
  learningPhase?: boolean
  abruptRevenue?: number
  weanedRevenue?: number
  revenueDifference?: number
}

/**
 * Strategy options for applying spend allocations.
 * 
 * @typedef {'weaned' | 'abrupt'} SubstitutionStrategy
 * - 'weaned': Gradual transition with performance-based allocation
 * - 'abrupt': Immediate complete switch at transition point
 */
export type SubstitutionStrategy = 'weaned' | 'abrupt'

/**
 * Generates simulated data showing the substitution effect between fatigued and new creatives.
 * 
 * This function models realistic creative performance patterns across three phases:
 * 1. Initial Phase (20%): Stable performance of original creative with late-stage fatigue
 * 2. Transition Phase (50%): Includes learning phase (20%) with high volatility, followed by gradual improvement
 * 3. Stabilization Phase (30%): Continued slow improvement with natural fluctuations
 * 
 * The generated data is strategy-agnostic - it represents the underlying performance
 * characteristics of both creatives. Strategy-specific spend allocations are applied
 * separately via applySubstitutionStrategy().
 *
 * @param {number} numPoints - Number of data points to generate (typically 100 for a 100-day timeline)
 * @param {RandomSource} [rng=Math.random] - Random number generator (defaults to Math.random)
 * @returns {SubstitutionEffectDataPoint[]} Array of data points with performance metrics
 * 
 * @example
 * // Generate deterministic data for testing
 * import { createDeterministicRandom, hashSeed } from '~/lib/utils/random'
 * const rng = createDeterministicRandom(hashSeed('test-scenario'))
 * const data = generateSubstitutionEffectData(100, rng)
 * 
 * @example
 * // Generate random data for visualization
 * const data = generateSubstitutionEffectData(100)
 */
export function generateSubstitutionEffectData(
  numPoints: number,
  rng: RandomSource = Math.random
): SubstitutionEffectDataPoint[] {
  const data: SubstitutionEffectDataPoint[] = []

  // Phase durations (as percentages of total timeline)
  const initialPhase = 0.2       // 20% - Initial stable performance
  const transitionPhase = 0.5    // 50% - Transition period (includes learning phase)
  const stabilizationPhase = 0.3 // 30% - Performance stabilization
  void stabilizationPhase

  // Calculate phase boundaries
  const initialPhaseEnd = Math.floor(numPoints * initialPhase)
  const transitionPhaseEnd = Math.floor(numPoints * (initialPhase + transitionPhase))
  
  // Learning phase duration (within transition phase)
  // Set to 20% of total timeline to show extended poor performance period
  const learningPhaseDuration = Math.floor(numPoints * 0.20)

  // Starting values
  let fatiguedCreativeROAS = 4.0
  let newCreativeROAS = 0
  let previousNewCreativeROAS = 0

  // Generate the underlying performance data for both creatives
  // This will be the same regardless of strategy (weaned vs abrupt)
  for (let i = 0; i < numPoints; i += 1) {
    // Initial phase: Stable performance of original creative
    if (i < initialPhaseEnd) {
      // Add more noticeable fatigue as we approach the transition point
      // This simulates the realistic decline that triggers creative replacement
      const approachingTransition = i / initialPhaseEnd
      if (approachingTransition > 0.7) { // Last 30% of initial phase
        fatiguedCreativeROAS = 4.0 - (approachingTransition - 0.7) * 1.5 + (rng() - 0.5) * 0.3
      } else {
        fatiguedCreativeROAS = 4.0 + (rng() - 0.5) * 0.5
      }
      newCreativeROAS = 0 // New creative not introduced yet
    }
    // Transition phase: Original creative declines, new creative introduced and improves
    else if (i < transitionPhaseEnd) {
      // Fatigued creative continues to decline more sharply
      const phaseProgress = (i - initialPhaseEnd) / (transitionPhaseEnd - initialPhaseEnd)
      fatiguedCreativeROAS -= 0.05 + phaseProgress * 0.08 + (rng() - 0.5) * 0.2
      fatiguedCreativeROAS = Math.max(fatiguedCreativeROAS, 1.0)

      // Determine if we're in the learning phase
      const isInLearningPhase = i >= initialPhaseEnd && i < (initialPhaseEnd + learningPhaseDuration)

      if (i === initialPhaseEnd) {
        // Initial introduction of new creative - start with poor performance
        newCreativeROAS = 0.5 + rng() * 0.5
        previousNewCreativeROAS = newCreativeROAS
      } else if (isInLearningPhase) {
        // During learning phase: POOR PERFORMANCE with HIGH volatility
        // This simulates the algorithm learning period with no predictable improvement
        
        // Store previous value to detect stagnation
        previousNewCreativeROAS = newCreativeROAS
        
        // Generate high volatility with no clear pattern
        // Use significant changes to avoid getting stuck at boundaries
        const volatilityFactor = 0.4 + rng() * 0.8 // Between 0.4 and 1.2
        const direction = rng() > 0.5 ? 1 : -1
        
        // Apply a significant change that prevents stagnation
        newCreativeROAS = newCreativeROAS + direction * volatilityFactor
        
        // If we're at the same value for too long, force a change
        // This ensures realistic volatility throughout the learning phase
        if (Math.abs(newCreativeROAS - previousNewCreativeROAS) < 0.1) {
          newCreativeROAS += rng() > 0.5 ? 0.3 : -0.3
        }
        
        // Keep it in a reasonable but low range during learning
        // Lower floor (0.1) allows for very poor performance during learning
        newCreativeROAS = Math.max(0.1, Math.min(2.5, newCreativeROAS))
        
        // Ensure we don't get stuck at boundaries
        // Push away from edges to maintain volatility
        if (newCreativeROAS < 0.25 || newCreativeROAS > 2.45) {
          newCreativeROAS = newCreativeROAS < 0.25 ? 0.3 + rng() * 0.4 : 2.4 - rng() * 0.4
        }
      } else {
        // After learning phase: SLOW and METHODICAL improvement
        // This represents the algorithm optimization phase with gradual gains
        
        // Calculate progress within post-learning phase
        const postLearningProgress = (i - (initialPhaseEnd + learningPhaseDuration)) /
          (transitionPhaseEnd - (initialPhaseEnd + learningPhaseDuration))
        
        // Target is to reach 3.8-4.0 by end of transition phase
        // This is a slower, more realistic improvement curve
        const targetValue = 3.8 + postLearningProgress * 0.2
        
        // Calculate distance to target for proportional steps
        const currentDistance = targetValue - newCreativeROAS
        
        // Determine if we should have a regression (ebb)
        // More likely early in this phase, decreasing over time
        const shouldRegress = rng() < (0.3 - postLearningProgress * 0.2)

        if (shouldRegress) {
          // Create a regression (temporary setback)
          // Simulates natural performance fluctuations
          newCreativeROAS -= rng() * 0.3
        } else {
          // Normal progress toward target with natural variation
          // Smaller step size for more gradual, realistic improvement
          const stepSize = Math.min(0.12, currentDistance * 0.04)
          const naturalVariation = (rng() - 0.5) * 0.3
          
          newCreativeROAS += stepSize + naturalVariation
        }
        
        // Ensure we don't exceed our target range too early
        // This prevents unrealistic rapid improvement
        if (postLearningProgress < 0.8) {
          newCreativeROAS = Math.min(newCreativeROAS, 3.5 + postLearningProgress * 0.5)
        }
        
        // Ensure we don't drop too low after learning phase
        // Maintains minimum performance floor that increases over time
        newCreativeROAS = Math.max(newCreativeROAS, 1.0 + postLearningProgress * 1.5)
      }
    }
    // Stabilization phase: New creative continues slow improvement with natural fluctuations
    else {
      // Fatigued creative continues minimal decline (though it's no longer used)
      fatiguedCreativeROAS -= 0.01 + (rng() - 0.5) * 0.1
      fatiguedCreativeROAS = Math.max(fatiguedCreativeROAS, 0.5)
      
      // Calculate progress within stabilization phase
      const stabilizationProgress = (i - transitionPhaseEnd) / (numPoints - transitionPhaseEnd)
      
      // Start from where we left off (around 3.8-4.0) and continue slow improvement
      // with natural ebbs and flows, approaching but not fixating on ~4.8
      
      // Determine if we should have a regression (ebb)
      // Occasional setbacks maintain realism
      const shouldRegress = rng() < 0.15

      if (shouldRegress) {
        // Create a more significant regression occasionally
        // Simulates market changes, audience shifts, etc.
        newCreativeROAS -= rng() * 0.4
      } else {
        // Natural variation that can go both up and down
        const naturalVariation = (rng() - 0.5) * 0.4
        
        // Very slight upward bias that diminishes over time
        // Represents continued optimization with diminishing returns
        const growthBias = 0.025 * Math.max(0, 1 - stabilizationProgress)
        
        // Apply changes
        newCreativeROAS += naturalVariation + growthBias
      }
      
      // Target range is 4.0-4.8 with natural fluctuations
      // Use a soft targeting approach to avoid obvious manipulation
      if (newCreativeROAS > 5.4 + rng() * 0.3) {
        // If we're too high, create a natural correction
        newCreativeROAS -= 0.1 + rng() * 0.3
      } else if (newCreativeROAS < 3.6 - rng() * 0.3) {
        // If we're too low, create a natural correction
        newCreativeROAS += 0.1 + rng() * 0.3
      }
      
      // Add occasional larger swings to prevent it looking too artificial
      // 5% chance of a larger swing simulates market volatility
      if (rng() < 0.05) {
        newCreativeROAS += (rng() - 0.5) * 0.7
      }
    }

    // Ensure values stay within reasonable bounds
    // Maximum ROAS of 5.8x represents exceptional performance ceiling
    fatiguedCreativeROAS = Math.max(0, Math.min(5.8, fatiguedCreativeROAS))
    newCreativeROAS = Math.max(0, Math.min(5.8, newCreativeROAS))

    // Determine if this point is in the learning phase
    const isLearningPhase = i >= initialPhaseEnd && i < (initialPhaseEnd + learningPhaseDuration)

    // Calculate spend allocations for both strategies
    // Both strategies start introducing the new creative at the same time (initialPhaseEnd)
    
    // Abrupt strategy: immediate complete switch at initialPhaseEnd
    const abruptFatiguedSpend = i < initialPhaseEnd ? 1000 : 0
    const abruptNewSpend = i < initialPhaseEnd ? 0 : 1000

    // Weaned strategy: gradual transition starting at initialPhaseEnd
    let weanedFatiguedSpend = 1000
    let weanedNewSpend = 0

    if (i >= initialPhaseEnd) {
      // Both strategies start introducing the new creative at initialPhaseEnd
      // For weaned strategy, start with a small allocation regardless of performance
      // and then gradually increase based on relative performance
      
      // Calculate days since transition started
      const daysSinceTransition = i - initialPhaseEnd
      
      // Base allocation that increases linearly with time (minimum 10% after initialPhaseEnd)
      // This ensures gradual transition even during poor performance
      const baseAllocationPercent = Math.min(0.5, 0.1 + daysSinceTransition / 50)
      
      // Performance-based additional allocation
      let performanceBasedAllocation = 0
      if (newCreativeROAS > 0) {
        /**
         * Fallback strategy for fatigued creative ROAS:
         * - If null, use 2.5x as a conservative estimate
         * - This represents the typical performance of a creative that has been
         *   phased out but whose last known performance was around this level
         * - 2.5x is chosen as it's below the initial 4.0x but above break-even,
         *   reflecting natural creative fatigue patterns
         */
        const fatiguedROAS = fatiguedCreativeROAS ?? 2.5 // Conservative fallback based on typical decline
        if (fatiguedROAS > 0) {
          /**
           * Performance-based allocation logic:
           * - When new creative performs equally (ratio = 1.0): no additional allocation
           * - When new creative performs better (ratio < 1.0): increase allocation
           * - When new creative performs worse (ratio > 1.0): decrease allocation
           * - Scale is capped at 0.5 (50% additional allocation maximum)
           * 
           * This creates a smooth transition that responds to relative performance
           * while maintaining the base allocation floor
           */
          const performanceRatio = fatiguedROAS / newCreativeROAS
          // Scale from 1.0 (equal performance) to 0.5 (half performance)
          performanceBasedAllocation = Math.max(0, 0.5 - Math.min(0.5, (performanceRatio - 0.5) / 0.5))
        }
        // If fatiguedROAS is 0 or null, performanceBasedAllocation remains 0
      }
      
      // Combined allocation percentage (base + performance-based)
      // Capped at 100% to prevent over-allocation
      const newCreativeAllocationPercent = Math.min(1, baseAllocationPercent + performanceBasedAllocation)
      
      weanedNewSpend = Math.round(1000 * newCreativeAllocationPercent)
      weanedFatiguedSpend = 1000 - weanedNewSpend
    }

    // Calculate weighted ROAS for both strategies
    // Weighted by spend allocation to show overall portfolio performance
    const abruptWeightedROAS =
      (abruptFatiguedSpend * fatiguedCreativeROAS + abruptNewSpend * newCreativeROAS) / 1000

    const weanedWeightedROAS =
      (weanedFatiguedSpend * fatiguedCreativeROAS + weanedNewSpend * newCreativeROAS) / 1000

    // Calculate revenue for both strategies
    // Revenue = Spend × ROAS for each creative
    const abruptRevenue = abruptFatiguedSpend * fatiguedCreativeROAS + abruptNewSpend * newCreativeROAS
    const weanedRevenue = weanedFatiguedSpend * fatiguedCreativeROAS + weanedNewSpend * newCreativeROAS
    const revenueDifference = weanedRevenue - abruptRevenue

    data.push({
      day: i,
      fatiguedCreativeSpend: 0, // Will be set based on selected strategy
      newCreativeSpend: 0, // Will be set based on selected strategy
      totalSpend: 1000, // Total spend remains constant
      fatiguedCreativeROAS,
      newCreativeROAS,
      weightedROAS: 0, // Will be set based on selected strategy
      abruptWeightedROAS,
      weanedWeightedROAS,
      learningPhase: isLearningPhase,
      abruptRevenue,
      weanedRevenue,
      revenueDifference,
    })
  }

  return data
}

/**
 * Applies a budget strategy to the generated data to determine spend allocation and weighted ROAS.
 * 
 * This function takes the base performance data and applies strategy-specific spend allocations:
 * - Weaned: Gradual transition with performance-based allocation
 * - Abrupt: Immediate complete switch at transition point
 * 
 * The function also sets ROAS values to null when a creative has zero spend allocation,
 * which is important for proper chart rendering and tooltip display.
 * 
 * @param {SubstitutionEffectDataPoint[]} baseData - Base performance data from generateSubstitutionEffectData
 * @param {SubstitutionStrategy} strategy - Strategy to apply ('weaned' or 'abrupt')
 * @param {number} numPoints - Total number of data points (used to calculate phase boundaries)
 * @returns {SubstitutionEffectDataPoint[]} Data with strategy-specific spend allocations applied
 * 
 * @example
 * const baseData = generateSubstitutionEffectData(100)
 * const weanedData = applySubstitutionStrategy(baseData, 'weaned', 100)
 * const abruptData = applySubstitutionStrategy(baseData, 'abrupt', 100)
 */
export function applySubstitutionStrategy(
  baseData: SubstitutionEffectDataPoint[],
  strategy: SubstitutionStrategy,
  numPoints: number
): SubstitutionEffectDataPoint[] {
  const initialPhaseEnd = Math.floor(numPoints * 0.2)

  return baseData.map(point => {
    if (strategy === 'weaned') {
      // Weaned strategy: gradually transition starting at initialPhaseEnd
      let fatiguedCreativeSpend = 1000
      let newCreativeSpend = 0

      if (point.day >= initialPhaseEnd) {
        // Calculate days since transition started
        const daysSinceTransition = point.day - initialPhaseEnd
        
        // Base allocation that increases linearly with time (minimum 10% after initialPhaseEnd)
        const baseAllocationPercent = Math.min(0.5, 0.1 + daysSinceTransition / 50)
        
        // Performance-based additional allocation with proper null handling
        let performanceBasedAllocation = 0
        if (point.newCreativeROAS && point.newCreativeROAS > 0) {
          /**
           * Fallback strategy for fatigued creative ROAS:
           * - If null, use 2.5x as a conservative estimate
           * - This represents the typical performance of a creative that has been
           *   phased out but whose last known performance was around this level
           * - 2.5x is chosen as it's below the initial 4.0x but above break-even,
           *   reflecting natural creative fatigue patterns
           */
          const fatiguedROAS = point.fatiguedCreativeROAS ?? 2.5
          
          // Only calculate performance-based allocation if we have meaningful data
          if (fatiguedROAS > 0) {
            // Calculate relative performance ratio
            const performanceRatio = fatiguedROAS / point.newCreativeROAS
            
            /**
             * Performance-based allocation logic:
             * - When new creative performs equally (ratio = 1.0): no additional allocation
             * - When new creative performs better (ratio < 1.0): increase allocation
             * - When new creative performs worse (ratio > 1.0): decrease allocation
             * - Scale is capped at 0.5 (50% additional allocation maximum)
             */
            performanceBasedAllocation = Math.max(0, 0.5 - Math.min(0.5, (performanceRatio - 0.5) / 0.5))
          }
          // If fatiguedROAS is 0, performanceBasedAllocation remains 0 (no additional allocation)
        }
        
        // Combined allocation percentage (base + performance-based)
        const newCreativeAllocationPercent = Math.min(1, baseAllocationPercent + performanceBasedAllocation)
        
        newCreativeSpend = Math.round(1000 * newCreativeAllocationPercent)
        fatiguedCreativeSpend = 1000 - newCreativeSpend
      }

      return {
        ...point,
        fatiguedCreativeSpend,
        newCreativeSpend,
        weightedROAS: point.weanedWeightedROAS,
        // Set ROAS to null when spend is zero for proper chart rendering
        fatiguedCreativeROAS: fatiguedCreativeSpend > 0 ? point.fatiguedCreativeROAS : null,
        newCreativeROAS: newCreativeSpend > 0 ? point.newCreativeROAS : null,
      }
    }

    // Abrupt strategy: immediate complete switch at initialPhaseEnd
    const fatiguedCreativeSpend = point.day < initialPhaseEnd ? 1000 : 0
    const newCreativeSpend = point.day < initialPhaseEnd ? 0 : 1000

    return {
      ...point,
      fatiguedCreativeSpend,
      newCreativeSpend,
      weightedROAS: point.abruptWeightedROAS,
      // Set ROAS to null when spend is zero for proper chart rendering
      fatiguedCreativeROAS: fatiguedCreativeSpend > 0 ? point.fatiguedCreativeROAS : null,
      newCreativeROAS: newCreativeSpend > 0 ? point.newCreativeROAS : null,
    }
  })
}

/**
 * Calculates the total revenue difference for a given strategy based on the precomputed values.
 * 
 * This aggregates the revenue difference across all data points to show the cumulative
 * financial impact of choosing one strategy over another. A positive value indicates
 * the weaned strategy generated more revenue; a negative value indicates the abrupt
 * strategy was more profitable.
 * 
 * @param {SubstitutionEffectDataPoint[]} data - Data points with revenue calculations
 * @returns {number} Total cumulative revenue difference across all data points
 * 
 * @example
 * const data = generateSubstitutionEffectData(100)
 * const totalDifference = calculateTotalRevenueDifference(data)
 * console.log(`Weaned strategy generated $${totalDifference.toLocaleString()} more revenue`)
 */
export function calculateTotalRevenueDifference(
  data: SubstitutionEffectDataPoint[]
): number {
  return data.reduce((sum, point) => sum + (point.revenueDifference ?? 0), 0)
}